import Button from "app/components/button/button";
import CircleLoader from "app/components/loader/CircleLoader";
import { alphaLinks } from "app/config";
import useAppNavigation from "app/hooks/useAppNavigate";
import { useTheme } from "app/shared";
import Discord from "assets/img/icons/Discord.svg";
import Closed from "assets/img/icons/Union.svg";
import CopyIcon from "assets/img/icons/copy.svg";
import X from "assets/img/icons/twitterX.svg";
import React, { MouseEventHandler, useEffect, useState } from "react";
import { FaCheck } from "react-icons/fa";
import { useGetReferralCodeQuery } from "store/api/new.api";
import { useAccount } from "wagmi";

import styles from "./dashboard-karma.module.scss";

type KarmaDashboardProps = {
  leaderboardRank?: number;
  totalKarma?: number;
  referralKarma?: number;
  discordKarma: number;
  karmaLoading: boolean;
  refferalLoading: boolean;
  leaderboardLoading: boolean;
};

const DashboardKarma: React.FC<KarmaDashboardProps> = ({
  leaderboardRank,
  totalKarma,
  referralKarma,
  discordKarma,
  karmaLoading,
  refferalLoading,
  leaderboardLoading,
}) => {
  const [referralLink, setReferralLink] = useState<string>("");
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const [questKarma, setQuestKarma] = useState<number | null>(null);
  const tweetText = `Reached new Karma level on @alphamind_labs! 🥳 Are you still behind? 😜 Join me building Karma while backing true #web3Alpha.`;
  const { address } = useAccount();
  const campaignId = "3ghuAYvu"; // Note: This is only for getReferralCode, not for karma totals
  const account = address || "";
  const { data, error, isLoading } = useGetReferralCodeQuery({
    account,
    campaignId,
  });
  let copyTimeout: ReturnType<typeof setTimeout>;
  const { theme } = useTheme();
  const { navigateToBuildKarma, navigareToMainPage } = useAppNavigation();

  useEffect(() => {
    if (totalKarma && referralKarma !== undefined) {
      return setQuestKarma(totalKarma - referralKarma);
    }
  }, [totalKarma, referralKarma]);

  useEffect(() => {
    if (data) {
      setReferralLink(`${alphaLinks.BUILD_KARMA}?invite=${data.ref_id}`);
    }
  }, [data]);

  const onQuestClick = () => navigateToBuildKarma();

  const handleCopyLink = () => {
    navigator.clipboard
      .writeText(referralLink)
      .then(() => {
        setIsCopied(true);
        copyTimeout = setTimeout(() => setIsCopied(false), 3000);
      })
      .catch((err) => {
        console.error("Failed to copy link: ", err);
      });
  };

  const shareOnTwitter: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      tweetText
    )}`;
    window.open(twitterUrl, "_blank", "noopener,noreferrer");
  };

  const onDiscordClick = () => {
    window.open("https://discord.com/invite/NB4hhuXkWz", "_blank");
  };

  return (
    <div
      className={`${styles.karmaDashboard} ${
        theme === "dark" ? styles.darkTheme : ""
      }`}
    >
      <div className={styles.header}>
        <div className={styles.rank}>
          {leaderboardLoading ? (
            <span>
              <CircleLoader isLoading={leaderboardLoading} colorType="dark" />
            </span>
          ) : (
            <span>{leaderboardRank || "N/A"}</span>
          )}
          <p>Leaderboard Rank</p>
        </div>
        <div className={styles.totalKarma}>
          {karmaLoading ? (
            <span>
              <CircleLoader colorType="dark" isLoading={karmaLoading} />
            </span>
          ) : (
            <span>{totalKarma?.toLocaleString() || "0"}</span>
          )}
          <p>Total Karma</p>
        </div>
      </div>

      <h2>
        Grow your Karma & <br /> Collect Rewards
      </h2>

      <div className={styles.questSection}>
        <div className={styles.label}>
          <p>
            Participate
            <br />
            in IDOs
          </p>
          <p>
            <span className={styles.text}>+4 000</span> Karma
            <br />
            <span className={styles.subText}>per IDO</span>
          </p>
        </div>
        <div className={styles.progressBar}>
          <div className={styles.progress} />
        </div>
        <div className={styles.btnWrapper}>
          <Button
            variant="pink"
            className={styles.questButton}
            onClick={() => navigareToMainPage()}
          >
            Go to IDO
          </Button>
        </div>
      </div>

      <div className={styles.questSection}>
        <div className={styles.label}>
          <p>
            Complete
            <br />
            Quest
          </p>
          <div className={styles.subLabel}>
            {karmaLoading ? (
              <span className={styles.text}>
                <CircleLoader colorType="dark" isLoading={karmaLoading} />
              </span>
            ) : (
              <span className={styles.text}>
                +800 <span>Karma</span>
              </span>
            )}
            <br />
            <span className={styles.subText}>min per complited task</span>
          </div>
        </div>
        <div className={styles.progressBar}>
          <div className={styles.progress} />
        </div>
        <div className={styles.btnWrapper}>
          <Button
            variant="pink"
            className={styles.questButton}
            onClick={onQuestClick}
          >
            Go to Quest
          </Button>
        </div>
      </div>

      <div className={styles.referralSection}>
        <div className={styles.label}>
          <p>
            Inviting
            <br />
            referrals
          </p>
          <div className={styles.subLabel}>
            {refferalLoading ? (
              <span className={styles.text}>
                <CircleLoader colorType="dark" isLoading={refferalLoading} />
              </span>
            ) : (
              <>
                <span className={styles.text}>+20%</span> Karma
                <br />
                <span className={styles.subText}>
                  from each active referral's Karma
                </span>
              </>
            )}
          </div>
        </div>
        <div className={styles.progressBar}>
          <div className={styles.progress} />
        </div>
        <div className={styles.btnWrapper}>
          {isCopied ? (
            <Button
              variant="pink"
              className={styles.copyLinkButton}
              onClick={handleCopyLink}
            >
              Copied
              <FaCheck />
            </Button>
          ) : (
            <Button
              variant="pink"
              className={styles.copyLinkButton}
              onClick={handleCopyLink}
            >
              Copy Link
              <img src={CopyIcon} alt="copy" />
            </Button>
          )}
          <Button
            variant="pink"
            className={styles.shareButton}
            onClick={shareOnTwitter}
          >
            Share in
            <img src={X} alt="x" />
          </Button>
        </div>
      </div>

      <div className={styles.discordSection}>
        <div className={styles.label}>
          <p>
            Get Discord
            <br />
            Supporter Role
          </p>
          <p>
            <span className={styles.text}>
              +{discordKarma.toLocaleString()}
            </span>{" "}
            Karma
            <br />
            <span className={styles.subText}>per week</span>
          </p>
        </div>
        <div className={styles.progressBar}>
          <div className={styles.progress} />
        </div>
        <div className={styles.btnWrapper}>
          <Button
            variant="pink"
            className={styles.discordButton}
            onClick={onDiscordClick}
          >
            Go to Discord
            <img src={Discord} alt="discord" />
          </Button>
        </div>
      </div>

      <div className={styles.discordSection}>
        <div className={styles.label}>
          <div className={styles.closed}>
            <img src={Closed} alt="closed" />
            <p className={styles.comingSoon}>Stake $MIND</p>
          </div>
          <p className={styles.comingSoon}>Coming Soon</p>
        </div>
        <div className={styles.progressBar}>
          <div className={styles.progress} style={{ width: 0 }} />
        </div>
      </div>
    </div>
  );
};

export default DashboardKarma;
